import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useEffect } from 'react';

import { db, functions } from '../../../config';

/**
 * Stan buildu bazy wiedzy AI per organizacja — sekcyjny.
 * Path: `hotels/{organizationId}/aiagents/build-state`.
 *
 * Model:
 * ```
 * {
 *   dirtySections: {
 *     hotelInfo: { dirty: true, dirtyAt, lastChange: 'hotel' },
 *     events: { dirty: true, dirtyAt, lastChange: 'events' },
 *     ...
 *   },
 *   lastBuildAt: Timestamp,
 * }
 * ```
 *
 * Każdy trigger zapisuje TYLKO swoją sekcję. Stefan widget czyta
 * snapshotem i pokazuje listę dirty sekcji w popoverze z checkboxami.
 */

export const KB_SECTIONS = [
  'hotelInfo',
  'amenities',
  'outlets',
  'products',
  'attractions',
  'businessCards',
  'events',
  'specialOffers',
  'goodPractices',
  'phoneBook',
  'wifi',
  'rules',
  'tvChannels',
  'faq',
  'loyalty',
  'socialMedia',
  'virtualTour',
  'reviews',
] as const;

export type KbSection = typeof KB_SECTIONS[number];

export interface DirtySectionState {
  dirty: boolean;
  dirtyAt: Timestamp | null;
  lastChange: string | null;
}

export interface KbBuildState {
  dirtySections: Partial<Record<KbSection, DirtySectionState>>;
  lastBuildAt: number | null;
}

const DEFAULT_STATE: KbBuildState = {
  dirtySections: {},
  lastBuildAt: null,
};

/** Realtime snapshot listener. */
export const useKbBuildState = (
  organizationId: string | null | undefined,
): UseQueryResult<KbBuildState, FirebaseError> => {
  const queryClient = useQueryClient();
  const queryKey = ['kb-build-state', organizationId] as const;

  useEffect(() => {
    if (!organizationId) return undefined;

    const ref = doc(db, 'hotels', organizationId, 'aiagents', 'build-state');
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          queryClient.setQueryData(queryKey, DEFAULT_STATE);
          return;
        }
        const data = snapshot.data() as Partial<KbBuildState> & {
          dirtySections?: Record<string, Partial<DirtySectionState>>;
        };
        const dirty: Partial<Record<KbSection, DirtySectionState>> = {};
        const incoming = data.dirtySections ?? {};
        for (const section of KB_SECTIONS) {
          const entry = incoming[section];
          if (entry) {
            dirty[section] = {
              dirty: !!entry.dirty,
              dirtyAt: (entry.dirtyAt as Timestamp | null) ?? null,
              lastChange: typeof entry.lastChange === 'string' ? entry.lastChange : null,
            };
          }
        }
        queryClient.setQueryData(queryKey, {
          dirtySections: dirty,
          lastBuildAt: typeof data.lastBuildAt === 'number' ? data.lastBuildAt : null,
        });
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[useKbBuildState] error', error);
        queryClient.setQueryData(queryKey, DEFAULT_STATE);
      },
    );

    return () => unsubscribe();
  }, [organizationId, queryClient]);

  return useQuery<KbBuildState, FirebaseError>({
    queryKey,
    queryFn: async () => queryClient.getQueryData<KbBuildState>(queryKey) ?? DEFAULT_STATE,
    enabled: !!organizationId,
    staleTime: Infinity,
    retry: 0,
  });
};

/** Lista aktualnie dirty sekcji (helper). */
export const getDirtySectionsList = (state: KbBuildState | undefined): KbSection[] => {
  if (!state) return [];
  return KB_SECTIONS.filter((section) => state.dirtySections[section]?.dirty === true);
};

interface RebuildInput {
  organizationId: string;
  sections: KbSection[];
}

interface RebuildResponse {
  success: boolean;
  rebuiltSections: KbSection[];
  generatedAt?: number;
}

/** Wywołuje callable `rebuildKnowledgeBaseSections` z wybraną listą sekcji. */
export const useRebuildKnowledgeBaseSections = (): UseMutationResult<RebuildResponse, FirebaseError, RebuildInput> =>
  useMutation<RebuildResponse, FirebaseError, RebuildInput>({
    mutationFn: async ({ organizationId, sections }) => {
      const call = httpsCallable<
        { hotelId: string; sections: KbSection[] },
        RebuildResponse
      >(functions, 'v3-rebuildKnowledgeBaseSections');
      const result = await call({ hotelId: organizationId, sections });
      return result.data;
    },
  });
